import React from 'react';
import { Package, ArrowRight } from 'lucide-react';
import { useProductStore } from '../../store/productStore';
import { useCheckoutStore } from '../../store/checkoutStore';

interface Props {
  onNext: () => void;
}

export const ProductSelector = ({ onNext }: Props) => {
  const products = useProductStore((state) => state.products);
  const setCurrentPage = useCheckoutStore((state) => state.setCurrentPage);
  const [showBumpOffer, setShowBumpOffer] = React.useState(false);
  const [selectedProduct, setSelectedProduct] = React.useState('');
  const [selectedBump, setSelectedBump] = React.useState('');

  const handleContinue = () => {
    if (selectedProduct) {
      setCurrentPage({
        id: crypto.randomUUID(),
        name: 'New Checkout Page',
        fields: [],
        productId: selectedProduct,
        templateId: 'modern-split',
        bumpOffer: showBumpOffer && selectedBump ? {
          id: crypto.randomUUID(),
          productId: selectedBump,
          enabled: false, // Set to false by default
          title: '',
          description: '',
          price: 0,
        } : undefined,
        theme: {
          primaryColor: '#1E293B',
          fontFamily: 'Inter',
        }
      });
      onNext();
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold mb-4">Select Main Product</h2>
        <div className="grid grid-cols-3 gap-4">
          {products.filter(p => p.type === 'main').map((product) => (
            <button
              key={product.id}
              onClick={() => setSelectedProduct(product.id)}
              className={`p-4 border rounded-xl text-left transition-all ${
                selectedProduct === product.id
                  ? 'border-[#1E293B] bg-gray-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-start justify-between">
                <Package className="w-5 h-5 text-gray-500" />
                <span className="text-lg font-semibold">₹{product.price.toFixed(2)}</span>
              </div>
              <h3 className="font-medium mt-3">{product.name}</h3>
              <p className="text-sm text-gray-500 mt-1">{product.description}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="border-t pt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Add Order Bump?</h2>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showBumpOffer}
              onChange={(e) => {
                setShowBumpOffer(e.target.checked);
                if (!e.target.checked) {
                  setSelectedBump('');
                }
              }}
              className="rounded border-gray-300 text-[#1E293B] focus:ring-[#1E293B]"
            />
            <span className="text-sm font-medium">Yes, add a bump offer</span>
          </label>
        </div>

        {showBumpOffer && (
          <div className="grid grid-cols-3 gap-4 mt-4">
            {products.filter(p => p.type === 'bump').map((product) => (
              <button
                key={product.id}
                onClick={() => setSelectedBump(product.id)}
                disabled={product.id === selectedProduct}
                className={`p-4 border rounded-xl text-left transition-all ${
                  selectedBump === product.id
                    ? 'border-[#1E293B] bg-gray-50'
                    : product.id === selectedProduct
                    ? 'opacity-50 cursor-not-allowed'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between">
                  <Package className="w-5 h-5 text-gray-500" />
                  <span className="text-lg font-semibold">₹{product.price.toFixed(2)}</span>
                </div>
                <h3 className="font-medium mt-3">{product.name}</h3>
                <p className="text-sm text-gray-500 mt-1">{product.description}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end pt-6 border-t">
        <button
          onClick={handleContinue}
          disabled={!selectedProduct || (showBumpOffer && !selectedBump)}
          className="btn flex items-center gap-2"
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};